import {
  AutomationsPresentationV1Edge,
  AutomationsPresentationV1Node
} from 'Automations/automationEditor/types'
import { getUpstreamNodesWithOutputs } from 'Automations/nodeDefinitions/helpers/getUpstreamNodesWithOutputs'
import { MissingUpstreamDataError } from 'Automations/nodeDefinitions/semanticTypes'
import {
  NodeType,
  ResolvedNodeDefinitions
} from 'Automations/nodeDefinitions/types'

/**
 * Evaluates a node's semantic-type input mapping against the data its upstream
 * nodes actually produce, returning the names of any input properties that
 * cannot be resolved. Empty when the node has no input mapping or every input
 * resolves.
 *
 * This is the node-level counterpart to the workflow-level generation check: it
 * pinpoints *which* node is missing *which* upstream data (e.g. a contract
 * review dropped into a quote-approval flow that never produces a contract),
 * rather than only reporting that generation failed somewhere.
 */
export const getMissingUpstreamData = ({
  node,
  nodes,
  edges,
  definitions
}: {
  node: AutomationsPresentationV1Node
  nodes: AutomationsPresentationV1Node[]
  edges: AutomationsPresentationV1Edge[]
  definitions: ResolvedNodeDefinitions
}): string[] => {
  const definition = node.type ? definitions[node.type as NodeType] : undefined

  if (!definition?.inputMapping) return []

  const upstreamNodes = getUpstreamNodesWithOutputs({
    nodes,
    edges,
    targetNodeId: node.id,
    definitions
  })

  try {
    definition.inputMapping({
      upstreamNodes,
      config: node.data
    })
    return []
  } catch (error) {
    // Only missing-upstream-data failures are reported here; any other
    // transform error is surfaced by the workflow-level generation check.
    if (error instanceof MissingUpstreamDataError) {
      return error.missingProperties
    }
    return []
  }
}
